// Capa de tipos y utilidades del Portal Tickets de Seguimiento.
// Los datos reales viven en Supabase (tablas tickets_*, ver migración
// 20260803120000_tickets_modelo.sql); la lógica de acceso está en tickets-store.tsx.
// Los ids se manejan como string en el front (los <select>/dataTransfer de HTML son
// strings); el store convierte a integer al escribir en BD.

export type Priority = "alta" | "media" | "baja" | "sin";

export type Ticket = {
  id: string;
  numero: number;
  nombre: string;
  pipelineId: string;
  etapaId: string;
  prioridad: Priority;
  categoriaId: string;
  propietarios: string[]; // auth_user_id de los usuarios asignados (multi-propietario)
  creadoPorId: string | null; // auth_user_id de quien dio de alta el ticket
  creadoPor?: string | null; // nombre de quien lo levantó (resuelto en el store para listado/detalle)
  solicitante: string; // nombre del solicitante principal (respaldo de texto)
  solicitantes: ContactoRef[]; // contactos CRM que reportan el ticket (multi); el primero = principal
  inmueble: string;
  descripcion: string;
  fechaCreacion: string;
  /** Cuándo entró a su etapa actual (tickets.fecha_etapa); null = sin columna o sin dato. */
  fechaEtapa?: string | null;
  /** Cuándo vence (tickets.fecha_vencimiento, horas hábiles calculadas en BD); null = sin columna o sin dato. */
  fechaVencimiento?: string | null;
  /** Primera vez que entró a una etapa en proceso (tickets.fecha_inicio_atencion); reinicia el reloj una vez. */
  fechaInicioAtencion?: string | null;
  fechaCierre: string | null;
  fuente: string;
  // Enlace opcional al CRM / inventario (se llena con los selectores reales, ver Fase 2b)
  entidadRelacionadaId: string | null;
  propiedadId: string | null;
  /**
   * Propiedades ligadas (tabla tickets_propiedades). La primera = la principal, que también vive
   * en tickets.id_propiedad. Sin la tabla en el ambiente: solo la de id_propiedad (con `inmueble`).
   */
  propiedades?: PropiedadRef[];
  actividad: ActividadTicket[];
  // Palomitas de la Conversación (tickets_lecturas). Un mensaje está leído si su fecha <= la
  // marca de la OTRA parte. null = nunca leyó (o la migración aún no está en el ambiente).
  contraparteLeidoHasta?: string | null; // cliente, agente o broker
  equipoLeidoHasta?: string | null; // equipo SOZU
  // ✓✓ gris: hasta cuándo le llegó a la app de la contraparte (migración 20261019153742).
  contraparteRecibidoHasta?: string | null;
};

// Renglón de tickets_actividad. `tipo` separa lo que escribió una persona ("nota") de lo que
// registra el sistema ("sistema" / "cambio_estado"); `visibleCliente` = respuesta que ve el cliente.
export type ActividadTicket = {
  id: string;
  fecha: string;
  autor: string;
  /** auth_user_id del autor; null = cliente desde su app o sistema. */
  autorId?: string | null;
  texto: string;
  /** Etapa a la que llevó un cambio de estado (solo filas nuevas; las viejas solo traen el texto). */
  etapaDestinoId?: string | null;
  audioUrl?: string | null;
  tipo?: string | null;
  visibleCliente?: boolean;
  /** Última edición del texto por su autor (5 minutos); null = nunca editado. */
  editadoEn?: string | null;
};

// `codigo` es la llave ESTABLE entre ambientes: los id son IDENTITY y el
// pipeline de la app es el 6 en dev y el 7 en produccion.
export type Pipeline = {
  id: string;
  nombre: string;
  descripcion: string;
  codigo: string | null;
  /** cliente | interno | agente (NULL en pipelines sin clasificar). Lo usa el ámbito de Analítica. */
  ambito?: string | null;
  /**
   * Buzón de una app (App Clientes, App Agentes, Reventa): los tickets entran solo desde la app.
   * Desde el panel no se crea ni se mueve un ticket aquí (lo bloquea también un trigger en BD).
   */
  soloApps?: boolean;
  /**
   * Al levantar un ticket aquí se muestra el asistente que lo redacta (opción del pipeline).
   * undefined = la migración aún no corre (se usa la regla por nombre, ver `muestraAsistente`).
   */
  mostrarAsistente?: boolean;
  /**
   * Pipeline de un condominio (`tickets_pipelines_condominio`, Ticket #1481): recibe los reportes
   * que el residente levanta desde la app de clientes y lo atiende su administración en el Portal
   * Condominio. Se le contesta al cliente como en `app_clientes`.
   */
  deCondominio?: boolean;
};

/** Mensaje único cuando alguien intenta crear o mover un ticket a un pipeline de apps. */
export const MENSAJE_PIPELINE_SOLO_APPS =
  "Este pipeline es solo de trabajo: sus tickets entran desde las apps. Para crear o mover un ticket usa otro pipeline.";

// Pipeline de los reportes que levantan los clientes desde la app. Es el unico
// donde se puede contestar al cliente: en los demas no hay a quien contestarle.
export const PIPELINE_APP_CLIENTES = "app_clientes";
export type Etapa = {
  id: string;
  pipelineId: string;
  nombre: string;
  orden: number;
  cerrada: boolean;
  /** abierta | en_proceso | resuelta | cerrada | descartada (NULL en pipelines recientes). */
  tipoSemantico?: string | null;
};
export type Categoria = {
  id: string;
  nombre: string;
  pipelineId: string | null;
  /**
   * Bandeja de Atención a Clientes (`tickets_categorias.bandeja_atencion`): solo en el pipeline de
   * la app de clientes. NULL = ninguna. Cambiar a un caso de bandeja pasa por la BD
   * (`atencion_reclasificar_caso`, ver `cruzaBandejaAtencion`).
   */
  bandejaAtencion?: "cobranza" | "productos" | null;
};
// Un "agente" del módulo = usuario real de la plataforma con acceso al portal de tickets.
export type Agente = { id: string; nombre: string; rol: string; email: string; telefono?: string | null };

// Referencia a un contacto del CRM (entidad relacionada) que reporta un ticket.
// id = entidades_relacionadas.id - telefono = número ya formateado para mostrar (con lada si aplica).
/**
 * Propiedad ligada a un ticket. `etiqueta` = "Proyecto - U-101" (mismo formato que la app de
 * clientes, ver tickets-propiedades.ts). Ids como string, igual que el resto del front.
 */
export type PropiedadRef = {
  id: string;
  etiqueta: string;
  idCuentaCobranza?: string | null;
  idProyecto?: string | null;
};

export type ContactoRef = { id: string; nombre: string; email?: string; telefono?: string | null };

export const PRIORIDADES: { id: Priority; nombre: string }[] = [
  { id: "alta", nombre: "Alta" },
  { id: "media", nombre: "Media" },
  { id: "baja", nombre: "Baja" },
  { id: "sin", nombre: "Sin prioridad" },
];

/**
 * Fuente de un ticket: catálogo `tickets_fuentes`. `tickets.fuente` guarda la CLAVE (FK); la
 * pantalla muestra la ETIQUETA. `manual` = las que se eligen a mano en el selector; las demás
 * las pone el sistema (portal o app que levantó el ticket) y no se cambian sin querer.
 */
export type FuenteTicket = {
  clave: string;
  etiqueta: string;
  manual: boolean;
  orden: number;
  activo: boolean;
  /** Etiquetas viejas que se guardaban como texto libre (transición). */
  alias: string[];
};

/** Claves que el código escribe o compara. Nunca se arma la fuente con el nombre de un menú. */
export const FUENTE = {
  portal: "portal_admin",
  correo: "correo",
  telefono: "telefono",
  whatsapp: "whatsapp",
  visitaSitio: "visita_sitio",
  portalCobranza: "portal_cobranza",
  portalProductos: "portal_productos",
  portalCondominio: "portal_condominio",
  portalBancos: "portal_bancos",
  altaDireccionInactivos: "portal_alta_direccion_inactivos",
  appClientes: "app_clientes",
  appAgentes: "app_agentes",
  /** Ticket levantado desde Monitoreo del dashboard de cambios (issue de Sentry). */
  dashboardSentry: "dashboard_sentry",
} as const;

export const FUENTE_DEFAULT: string = FUENTE.portal;

/**
 * Respaldo mientras el catálogo carga (o si el ambiente aún no tiene la tabla): mismas claves y
 * etiquetas que la migración. El catálogo de BD manda en cuanto llega.
 */
export const FUENTES_RESPALDO: FuenteTicket[] = [
  { clave: FUENTE.portal, etiqueta: "Portal", manual: true, orden: 10, activo: true, alias: ["Portal"] },
  { clave: FUENTE.correo, etiqueta: "Correo", manual: true, orden: 20, activo: true, alias: ["Correo"] },
  { clave: FUENTE.telefono, etiqueta: "Teléfono", manual: true, orden: 30, activo: true, alias: ["Teléfono", "Telefono"] },
  { clave: FUENTE.whatsapp, etiqueta: "WhatsApp", manual: true, orden: 40, activo: true, alias: ["WhatsApp"] },
  { clave: FUENTE.visitaSitio, etiqueta: "Visita en sitio", manual: true, orden: 50, activo: true, alias: ["Visita en sitio"] },
  { clave: FUENTE.portalCobranza, etiqueta: "Portal Cobranza", manual: false, orden: 60, activo: true, alias: ["Portal Cobranza", "Portal de cobranza"] },
  { clave: FUENTE.portalProductos, etiqueta: "Portal Productos", manual: false, orden: 70, activo: true, alias: ["Portal Productos", "Portal de productos"] },
  { clave: FUENTE.portalCondominio, etiqueta: "Portal Condominio", manual: false, orden: 75, activo: true, alias: [] },
  { clave: FUENTE.portalBancos, etiqueta: "Portal Bancos", manual: false, orden: 80, activo: true, alias: ["Portal Bancos", "Portal de bancos"] },
  { clave: FUENTE.altaDireccionInactivos, etiqueta: "Portal Alta Dirección - Usuarios inactivos", manual: false, orden: 90, activo: true, alias: ["Alta Dirección · Usuarios inactivos"] },
  { clave: FUENTE.appClientes, etiqueta: "App de clientes", manual: false, orden: 100, activo: true, alias: ["App Clientes"] },
  { clave: FUENTE.appAgentes, etiqueta: "App de agentes", manual: false, orden: 110, activo: true, alias: ["App Agentes"] },
  { clave: "cambio_datos_app", etiqueta: "Cambio de datos desde la app", manual: false, orden: 120, activo: true, alias: ["App"] },
  { clave: "documento_expediente", etiqueta: "Documento del expediente", manual: false, orden: 130, activo: true, alias: ["Documento del expediente"] },
  { clave: "editar_comprador", etiqueta: "Editar comprador", manual: false, orden: 140, activo: true, alias: ["Editar Comprador"] },
  { clave: "simulacion_credito", etiqueta: "Simulación de crédito", manual: false, orden: 150, activo: true, alias: ["Margot Web", "Bottura Web", "SOZU Web", "Daiku Web", "Monócolo Web"] },
  { clave: FUENTE.dashboardSentry, etiqueta: "Dashboard - Sentry", manual: false, orden: 160, activo: true, alias: [] },
  { clave: "otro", etiqueta: "Otro", manual: false, orden: 990, activo: true, alias: [] },
];

const norm = (s: string) => s.trim().toLowerCase();

/**
 * Clave de un valor de `tickets.fuente`. Ya es clave → igual; etiqueta o alias viejo → su clave
 * (filas o ambientes aún sin migrar); desconocido → el mismo texto. Vacío → la default.
 */
export function claveFuente(valor: string | null | undefined, fuentes: FuenteTicket[] = FUENTES_RESPALDO): string {
  if (!valor) return FUENTE_DEFAULT;
  const n = norm(valor);
  for (const lista of [fuentes, FUENTES_RESPALDO]) {
    const f =
      lista.find((x) => x.clave === valor) ??
      lista.find((x) => norm(x.etiqueta) === n || (x.alias ?? []).some((a) => norm(a) === n));
    if (f) return f.clave;
  }
  return valor;
}

/**
 * Chip de origen en la tarjeta y el detalle: solo cuando NO se levantó a mano en el portal (lo
 * normal no se marca). Los del dashboard de cambios van resaltados.
 */
export function origenTicket(
  valor: string | null | undefined,
  fuentes: FuenteTicket[] = FUENTES_RESPALDO,
): { etiqueta: string; dashboard: boolean } | null {
  const clave = claveFuente(valor, fuentes);
  if (clave === FUENTE_DEFAULT) return null;
  return { etiqueta: etiquetaFuente(clave, fuentes), dashboard: clave.startsWith("dashboard") };
}

/** Etiqueta para mostrar. Sin match en el catálogo se muestra el valor tal cual. */
export function etiquetaFuente(valor: string | null | undefined, fuentes: FuenteTicket[] = FUENTES_RESPALDO): string {
  if (!valor) return "";
  const clave = claveFuente(valor, fuentes);
  return (
    fuentes.find((f) => f.clave === clave)?.etiqueta ??
    FUENTES_RESPALDO.find((f) => f.clave === clave)?.etiqueta ??
    valor
  );
}

/**
 * Opciones del selector: las manuales activas, más `actual` al inicio si no es manual (la que
 * puso el portal o la app: se ve y se conserva; `manual: false` permite deshabilitarla).
 */
export function opcionesFuente(
  fuentes: FuenteTicket[],
  actual?: string | null,
): { value: string; label: string; manual: boolean }[] {
  const manuales = fuentes
    .filter((f) => f.manual && f.activo)
    .sort((a, b) => a.orden - b.orden)
    .map((f) => ({ value: f.clave, label: f.etiqueta, manual: true }));
  const clave = actual ? claveFuente(actual, fuentes) : null;
  if (clave && !manuales.some((o) => o.value === clave)) {
    return [{ value: clave, label: etiquetaFuente(clave, fuentes), manual: false }, ...manuales];
  }
  return manuales;
}

export function antiguedad(iso: string) {
  const dias = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (dias < 1) return "Abierto hoy";
  if (dias < 30) return `Abierto por ${dias} día${dias === 1 ? "" : "s"}`;
  const meses = Math.floor(dias / 30);
  if (meses < 12) return `Abierto por ${meses} mes${meses === 1 ? "" : "es"}`;
  const anios = Math.floor(meses / 12);
  return `Abierto por ${anios === 1 ? "un año" : `${anios} años`}`;
}

const TZ = "America/Mexico_City";

export function fechaCorta(iso: string) {
  return new Date(iso).toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: TZ,
  });
}

export function fechaLarga(iso: string) {
  return new Date(iso).toLocaleString("es-MX", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: TZ,
  });
}

// `fecha_creacion` es una FECHA (el form la guarda como medianoche UTC). Se formatea en UTC y
// SIN hora → no se recorre un día al convertir a hora local (evita el bug de zona horaria).
// NO usar fechaCorta/fechaLarga aquí: esas van en hora local (correcto para timestamps reales
// como actividad o fecha de cierre, pero recorren el día en una fecha guardada a medianoche UTC).
/** Día (`YYYY-MM-DD`) de una fecha ISO en la Ciudad de México; vacío si no es una fecha. */
export function diaCdmx(iso: string | null | undefined): string {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? new Intl.DateTimeFormat("en-CA", { timeZone: "America/Mexico_City" }).format(d) : "";
}

export function fechaCreada(iso: string) {
  return new Date(iso).toLocaleDateString("es-MX", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function iniciales(nombre: string) {
  return nombre
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join("");
}
